'use client';
import {FC, useState} from 'react';
import styles from './NavBar.module.scss';
import Image from 'next/image';
import Logo from '../../app/assets/icons/logo.svg';
import {useAppDispatch} from '@/app/redux/hooks';
import Link from 'next/link';
import {useLocale, useTranslations} from 'next-intl';
import Letter from '../../app/assets/icons/letter.svg';
import Twitter from '../../app/assets/icons/twitter.svg';
import Telegram from '../../app/assets/icons/telegram-2.svg';
import Instagram from '../../app/assets/icons/instagram.svg';
import AuthPopup from '../AuthPopup';
import WaitlistPopupContainer from "@/shared/WaitlistPopupContainer";
import {usePathname, useRouter} from "@/i18n/routing";
import {useLocalStorage} from "usehooks-ts";

interface NavBarProps {
  className?: string;
  isMobile?: boolean;
  isOpened: boolean;
  setIsOpened: (isOpened: boolean) => void;
}

const NavBar: FC<NavBarProps> = ({className = '', isMobile = false, isOpened, setIsOpened}) => {
  const [isAuthPopupOpen, setIsAuthPopupOpen] = useState<boolean>(false);
  // const {isOpenMenu} = useAppSelector((state) => state.authorization);
  const t = useTranslations('BURGER_MENU');
  const ft = useTranslations('FOOTER_COMPONENT');
  const [token, setToken] = useLocalStorage<string>('token', '');
  const [email, setEmail] = useLocalStorage<string>('email', '');
  const [waitlistPopupOpen, setWaitlistPopupOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();


  const handleChangeLanguage = (e: any) => {
    e.preventDefault()
    if (locale.includes('en')) {
      router.replace({pathname}, {locale: 'ua'});
    } else {
      router.replace({pathname}, {locale: 'en'});
    }
  };

  const year = new Date().getFullYear();

  return (
    <aside
      className={`${styles.root} ${
        isOpened && isMobile ? styles.root__open : styles.root__closed
      } ${className}`}>
      <Link prefetch={false} className={styles.navbar__logo} href="/">
        <Image src={Logo} alt="logo"/>
      </Link>

      <nav className={styles.navbar__links}>
        <div className={styles.links__container}>
          <b className={styles.links__title}>{email || t("group.account")}</b>
          {token && <>
              <Link className={styles.links__elem} href={"/tickets"}
                    onClick={() => setIsOpened(false)}>
                {t('tickets')}
              </Link>
              <a href="#" className={styles.links__elem} onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}>{t("logout")}</a>
          </>}
          {!token &&
              <a className={styles.links__elem} href={'#'} onClick={() => {
                setIsOpened(false)
                setIsAuthPopupOpen(true)
              }}>{t('login')}</a>}
          <Link className={styles.links__elem} href={'#'} onClick={handleChangeLanguage}>{t('switch_language')}</Link>
        </div>
        <div className={styles.links__container}>
          <b className={styles.links__title}>{ft("legal")}</b>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_PRIVACY_POLICY_URL || "#"}
                onClick={() => setIsOpened(false)}>
            {ft('privacy_policy')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_PUBLIC_OFFER_URL || "#"}
                onClick={() => setIsOpened(false)}>
            {ft('public_offer')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_TICKET_PURCHASE_POLICY_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('ticket_purchase_policy')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_EVENT_POSTING_GUIDELINES_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('event_posting_guidelines')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_SUBSCRIPTION_PURCHASE_POLICY_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('subscription_purchase_policy')}
          </Link>
        </div>
        <div className={styles.links__container}>
          <b className={styles.links__title}>{ft("links")}</b>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_CONSOLE_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('console_for_organizers')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_DOCS_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('knowledge_base')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_TICKET_REFUND_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('how_to_refund_a_ticket')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_HOW_TO_CREATE_EVENT_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('how_to_create_an_event')}
          </Link>
          <Link className={styles.links__elem} href={process.env.NEXT_PUBLIC_HOW_TO_SELL_WITH_TELEGRAM_URL || "#"}
                onClick={() => setIsOpened(false)} target={"_blank"}>
            {ft('how_to_sell_tickets_with_telegram')}
          </Link>
        </div>

        {/*<div className={styles.links__container}>*/}
        {/*  <ButtonElement color="dark" className={styles.organizer} text={'sign in'}*/}
        {/*                 onClick={() => setWaitlistPopupOpen(true)}>*/}
        {/*    <span className={styles.signin__icon}>*/}
        {/*      <Image src={mountain} alt="mountain"/>*/}
        {/*    </span>*/}
        {/*    Organizers room*/}
        {/*  </ButtonElement>*/}
        {/*</div>*/}

        <div className={styles.social}>
          <a
            className={`${styles.social__link}`}
            target="_blank"
            href={process.env.NEXT_PUBLIC_SUPPORT_URL}>
            <Image src={Letter} alt="Letter"/>
          </a>
          <a className={styles.social__link} target="_blank" href={process.env.NEXT_PUBLIC_TELEGRAM_URL}>
            <Image src={Telegram} alt="telegram"/>
          </a>
          <a className={styles.social__link} target="_blank" href={process.env.NEXT_PUBLIC_TWITTER_URL}>
            <Image src={Twitter} alt="Twitter"/>
          </a>
          <a className={styles.social__link} target="_blank" href={process.env.NEXT_PUBLIC_INSTAGRAM_URL}>
            <Image src={Instagram} alt="Instagram"/>
          </a>

        </div>

        <footer className={styles.footer}>
          <p className={styles.text__default}>
            {ft('use')} © 2022-{year}<br/>
          </p>
        </footer>
      </nav>

      <AuthPopup setIsOpen={() => setIsAuthPopupOpen(false)} isOpen={isAuthPopupOpen}/>
      <WaitlistPopupContainer
        isOpen={waitlistPopupOpen}
        setIsOpen={setWaitlistPopupOpen}
      />
    </aside>
  );
};

export default NavBar;
